// src/App.js
import React, { useState, useEffect } from "react";
import "./App.css";
import "./authStyles.css";

import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Container } from "react-bootstrap";
import { Amplify, Auth, Hub } from "aws-amplify";
import { AnimatePresence } from "framer-motion";
import { getSubscriptionStatus } from "./lib/subscription";
import AdminInventory from "./pages/AdminInventory/AdminInventory";
import AdminOrders from "./pages/AdminOrders/AdminOrders";
import awsconfig from "./aws-exports";

import NavbarComponent from "./components/Navbar";
import Footer from "./components/MyFooter";
import AccountOverview from "./pages/AccountOverview/AccountOverview";
import FloatingPawButton from "./components/FloatingPawButton";
import ProtectedRoute from "./components/ProtectedRoute";
import HealthTimelinePage from "./pages/HealthTimelinePage/HealthTimelinePage";
import Home from "./pages/Home/Home";
import SubscribePage from "./pages/Subscribe/SubscribePage";
import UserProfile from "./components/UserProfile";
import LoginPage from "./pages/Auth/LoginPage";
import SmartTagSetup from "./pages/SmartTag/SmartTagSetup";
import PawChat from "./pages/Chat/PawChat";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService/TermsOfService";
import Redirect from "./Redirect";
import ContactSupport from "./pages/ContactSupport/ContactSupport";
import AboutPage from "./pages/AboutPage/AboutPage";
import FeedingSchedulePage from "./pages/Feeding/FeedingSchedulePage";
import BillingSuccess from "./pages/Billing/BillingSuccess";
import BillingCancel from "./pages/Billing/BillingCancel";
import AdminSupport from "./pages/AdminSupport/AdminSupport";
import NotesPage from "./pages/NotesPage/NotesPage";
import AdminTags from "./pages/AdminTags/AdminTags";
import OrderSuccessPage from "./pages/OrderSuccess/OrderSuccessPage";
import PublicPlansPage from "./pages/Plans/PublicPlansPage";
import AdminXReplies from "./pages/AdminXReplies/AdminXReplies";
import AdminDashboard from "./pages/AdminDashboard/AdminDashboard";
import AdminXPosts from "./pages/AdminXPosts/AdminXPosts";
import AdminTagBatches from "./pages/AdminTagBatches/AdminTagBatches";
import LostPetTag from "./pages/LostPetTag/LostPetTag";
import FoundPetPage from "./pages/FoundPetPage/FoundPetPage";


Amplify.configure(awsconfig);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasActiveSub, setHasActiveSub] = useState(false);

  const [subInfo, setSubInfo] = useState({
    status: "none",
    planName: null,
    maxTags: 0,
    usedTags: 0,
  });

  const [subChecked, setSubChecked] = useState(false);

  const checkSubscription = async () => {
    try {
      const res = await getSubscriptionStatus();
      const isActive = res?.status === "active";
      setHasActiveSub(isActive);

      const maxTags =
        typeof res?.maxTags === "number"
          ? res.maxTags
          : Number(res?.petLimit ?? 0);

      const usedTags =
        typeof res?.usedTags === "number"
          ? res.usedTags
          : Number(res?.usedTags ?? 0);

      setSubInfo({
        status: res?.status || "none",
        planName: res?.planName || null,
        maxTags: Number.isNaN(maxTags) ? 0 : maxTags,
        usedTags: Number.isNaN(usedTags) ? 0 : usedTags,
      });
    } catch (err) {
      console.error("Global subscription check failed:", err);
      setHasActiveSub(false);
      setSubInfo({
        status: "none",
        planName: null,
        maxTags: 0,
        usedTags: 0,
      });
    } finally {
      setSubChecked(true);
    }
  };

  useEffect(() => {
    const listener = (data) => {
      switch (data.payload.event) {
        case "signIn":
          setIsAuthenticated(true);
          setSubChecked(false);
          checkSubscription();
          break;
        case "signOut":
          setIsAuthenticated(false);
          setHasActiveSub(false);
          setSubChecked(false);
          break;
        default:
          break;
      }
    };

    Hub.listen("auth", listener);

    const checkAuthState = async () => {
      try {
        await Auth.currentAuthenticatedUser();
        setIsAuthenticated(true);
        setSubChecked(false);
        await checkSubscription();
      } catch {
        setIsAuthenticated(false);
        setHasActiveSub(false);
      }
    };

    checkAuthState();

    return () => {
      Hub.remove("auth", listener);
    };
  }, []); // intentionally not adding checkSubscription to avoid loops

  return (
    <Router>
      <AnimatePresence>
        <AppContent
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
          hasActiveSub={hasActiveSub}
          subInfo={subInfo}
          subChecked={subChecked}
        />
      </AnimatePresence>
    </Router>
  );
}

/**
 * Inner layout component that can use useLocation
 * to decide when to show/hide footer, etc.
 */
function AppContent({
  isAuthenticated,
  setIsAuthenticated,
  hasActiveSub,
  subInfo,
  subChecked,
}) {
  const location = useLocation();

  // Routes that should feel like an "app" (no footer, full-screen vibe)
  const APP_ROUTES = [
    "/smarttag",
    "/health",
    "/profile",
    "/overview",
    "/notes",
    "/pawchat",
    "/admin",
    "/contact-support",
    "/found",
    "/feeding",
    "/login"
  ];

  const isAppRoute = APP_ROUTES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );

  // Show footer only on non-app routes (pure marketing/public pages)
  const showFooter = !isAppRoute;

  return (
    <div className="app-container">
      {/* Navigation */}
      <NavbarComponent
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
        hasActiveSub={hasActiveSub}
      />

      {/* Main content */}
         <div className="main-content">
      <Container className="mt-4">
        <Routes>
          {/* Public/marketing routes */}
          <Route path="/" element={<Home isAuthenticated={isAuthenticated} />} />

          <Route path="/login" element={<LoginPage />} />
<Route path="/smarttag/:tagId" element={<LoginPage />} />  // 👈 QR + AC link

<Route path="/plans" element={<PublicPlansPage />} />
<Route path="/found" element={<FoundPetPage />} />
          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/billing/cancel" element={<BillingCancel />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact-support" element={<ContactSupport />} />
          <Route path="/redirect/:slug" element={<Redirect />} />
          <Route path="/order-success" element={<OrderSuccessPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
<Route path="/lost-pet-tag" element={<LostPetTag  />} />
          {/* Public finder route – NO ProtectedRoute */}
          <Route
            path="/found/:tagId"
            element={<SmartTagSetup finderMode={true} />}
          />

          {/* Protected routes (login + subscription where required) */}

          <Route
  path="/smarttag"
  element={
    <ProtectedRoute  hasActiveSub={hasActiveSub}
                subChecked={subChecked}
                requireSub={true}>
      <SmartTagSetup subInfo={subInfo} />
    </ProtectedRoute>
  }
/>

   <Route
  path="/feeding"
  element={
    <ProtectedRoute hasActiveSub={hasActiveSub} subChecked={subChecked}>
      <FeedingSchedulePage />
    </ProtectedRoute>
  }
/>


<Route
  path="/profile"
  element={
    <ProtectedRoute hasActiveSub={hasActiveSub} subChecked={subChecked}>
      <UserProfile />
    </ProtectedRoute>
  }
/>


          <Route
            path="/overview"
            element={
              <ProtectedRoute
                hasActiveSub={hasActiveSub}
                subChecked={subChecked}
                requireSub={true}
              >
                <AccountOverview subInfo={subInfo} />
              </ProtectedRoute>
            }
          />


<Route
  path="/admin/x-replies"
  element={
    <ProtectedRoute hasActiveSub={hasActiveSub}>
      <AdminXReplies />
    </ProtectedRoute>
  }
/>
<Route
  path="/admin"
  element={
    <ProtectedRoute requireSupport={true}>
      <AdminDashboard />
    </ProtectedRoute>
  }
/>



          <Route
            path="/health"
            element={
              <ProtectedRoute
                hasActiveSub={hasActiveSub}
                subChecked={subChecked}
                requireSub={true}
              >
                <HealthTimelinePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notes"
            element={
              <ProtectedRoute
                hasActiveSub={hasActiveSub}
                subChecked={subChecked}
                requireSub={true}
              >
                <NotesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/pawchat"
            element={
              <ProtectedRoute
                hasActiveSub={hasActiveSub}
                subChecked={subChecked}
                requireSub={true}
              >
                <PawChat />
              </ProtectedRoute>
            }
          />
<Route
  path="/admin/x-posts"
  element={
    <ProtectedRoute requireSupport={true}>
      <AdminXPosts />
    </ProtectedRoute>
  }
/>
<Route
  path="/admin/tag-batches"
  element={
    <ProtectedRoute requireSupport={true}>
      <AdminTagBatches />
    </ProtectedRoute>
  }
/>


          {/* Admin pages – only require login (no subscription gate) */}
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute hasActiveSub={hasActiveSub}>
                <AdminSupport />
              </ProtectedRoute>
            }
          />

             <Route
            path="/admin/inventory"
            element={
              <ProtectedRoute hasActiveSub={hasActiveSub}>
               <AdminInventory />
              </ProtectedRoute>
            }


          />

          <Route
  path="/admin/orders"
  element={
    <ProtectedRoute hasActiveSub={hasActiveSub}>
      <AdminOrders />
    </ProtectedRoute>
  }
/>


          <Route
            path="/admin/tags"
            element={
              <ProtectedRoute hasActiveSub={hasActiveSub}>
                <AdminTags />
              </ProtectedRoute>
            }
          />
        </Routes>



      </Container>
</div>
      {/* Footer only on non-app routes */}
      {showFooter && <Footer />}


    </div>
  );
}

export default App;
